import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounced } from "./useDebounced";

// Client-side navigation for the desk. The router already listens to popstate,
// so a pushState plus a synthetic popstate moves every subscriber at once.

export function navigate(to: string, replace = false, state: unknown = null): void {
	window.history[replace ? "replaceState" : "pushState"](state, "", to);
	window.dispatchEvent(new PopStateEvent("popstate"));
}

export interface Loc {
	path: string;
	search: string;
}

const read = (): Loc => ({
	path: window.location.pathname,
	search: window.location.search,
});

export function useLoc(): Loc {
	const [loc, setLoc] = useState<Loc>(read);
	useEffect(() => {
		const onChange = () => setLoc(read());
		window.addEventListener("popstate", onChange);
		return () => window.removeEventListener("popstate", onChange);
	}, []);
	return loc;
}

/** Returns `search` with one param set or removed (pure). */
export function withParam(search: string, key: string, value: string | null): string {
	return withParams(search, { [key]: value });
}

/** Returns `search` with each param set, or removed when null or empty (pure). */
export function withParams(search: string, changes: Record<string, string | null>): string {
	const q = new URLSearchParams(search);
	for (const [key, value] of Object.entries(changes)) {
		if (value === null || value === "") q.delete(key);
		else q.set(key, value);
	}
	const s = q.toString();
	return s ? `?${s}` : "";
}

/**
 * Change query params, keeping the path and hash. Discrete choices push so Back undoes them;
 * pass `replace` for anything that changes per keystroke.
 */
export function setParams(changes: Record<string, string | null>, replace = false): void {
	const { pathname, search, hash } = window.location;
	const next = withParams(search, changes);
	if (next === search) return;
	navigate(`${pathname}${next}${hash}`, replace, replace ? window.history.state : null);
}

/** One query param as state. The fallback never appears in the URL, so links stay short. */
export function useParam(key: string, fallback = ""): [string, (value: string | null, replace?: boolean) => void] {
	const { search } = useLoc();
	const value = new URLSearchParams(search).get(key) ?? fallback;
	const set = useCallback(
		(next: string | null, replace = false) => setParams({ [key]: next === fallback ? null : next }, replace),
		[key, fallback],
	);
	return [value, set];
}

const TYPING_DEBOUNCE_MS = 200;

/**
 * Text that lives in the URL. The input updates on every key and the URL follows after a
 * pause, by replace, so typing does not fill the history. Back and Forward still reach the
 * input. `alias` is an older name for the param, read but never written.
 */
export function useSearchParam(key = "q", alias?: string): [string, (v: string) => void, string] {
	const { search } = useLoc();
	const params = new URLSearchParams(search);
	const inUrl = params.get(key) ?? (alias ? params.get(alias) : null) ?? "";
	const [text, setText] = useState(inUrl);
	const written = useRef(inUrl);

	// Only outside changes count: echoing our own write back would undo newer keystrokes.
	useEffect(() => {
		if (inUrl !== written.current) {
			written.current = inUrl;
			setText(inUrl);
		}
	}, [inUrl]);

	const settled = useDebounced(text, TYPING_DEBOUNCE_MS);
	useEffect(() => {
		const value = settled.trim();
		if (value === written.current.trim()) return;
		written.current = value;
		setParams({ [key]: value || null, ...(alias ? { [alias]: null } : {}) }, true);
	}, [settled, key, alias]);

	return [text, setText, settled.trim()];
}

export function openMarket(conditionId: string): void {
	const { pathname, search, hash } = window.location;
	navigate(`${pathname}${withParams(search, { m: conditionId, mt: null, w: null })}${hash}`, false, { drawer: true });
}

/** A real URL for the wallet drawer over the current view, so a new tab works too. */
export function walletHref(address: string): string {
	const { pathname, search, hash } = window.location;
	return `${pathname}${withParams(search, { w: address, m: null, mt: null })}${hash}`;
}

/** One drawer at a time. The one it replaces sits behind it in history, so Back unwinds the trail. */
export function openWallet(address: string): void {
	navigate(walletHref(address), false, { drawer: true });
}

/** Opened here, the entry behind this one is where you came from, so Back and close agree. */
export function closeDrawer(): void {
	if ((window.history.state as { drawer?: boolean } | null)?.drawer) {
		window.history.back();
		return;
	}
	const { pathname, search, hash } = window.location;
	navigate(`${pathname}${withParams(search, { m: null, mt: null, w: null })}${hash}`, true);
}

export const closeMarket = closeDrawer;

export function Link({
	href,
	className,
	title,
	children,
	onClick,
	"aria-current": ariaCurrent,
}: {
	href: string;
	className?: string;
	title?: string;
	children: ComponentChildren;
	onClick?: () => void;
	"aria-current"?: "page";
}) {
	return (
		<a
			href={href}
			className={className}
			title={title}
			aria-current={ariaCurrent}
			onClick={(e: MouseEvent) => {
				if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
				e.preventDefault();
				onClick?.();
				navigate(href);
			}}
		>
			{children}
		</a>
	);
}
